import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

function clean(value) {
  return String(value ?? "").trim();
}

function normalizeCards(value) {
  const cards =
    Array.isArray(value)
      ? value
      : [];

  return cards.map(
    (card) => ({
      cardId:
        String(card?.cardId ?? ""),
      title:
        String(card?.title ?? ""),
      summary:
        String(card?.summary ?? ""),
    })
  );
}

function sameCards(left, right) {
  if (
    left.length !== right.length
  ) {
    return false;
  }

  return left.every(
    (card, index) => {
      const other =
        right[index];

      return (
        String(card?.cardId ?? "") ===
          String(other?.cardId ?? "") &&
        String(card?.title ?? "") ===
          String(other?.title ?? "") &&
        String(card?.summary ?? "") ===
          String(other?.summary ?? "")
      );
    }
  );
}

function newCardId() {
  return [
    "story",
    Date.now().toString(36),
    Math.random()
      .toString(36)
      .slice(2, 10),
  ].join("-");
}

export default function ScreenplayStoryMap({
  projectPlan,
  onSave,
  canEdit = true,
  isFR = false,
}) {
  const persistedCards =
    useMemo(
      () =>
        normalizeCards(
          projectPlan?.storyMap?.cards
        ),
      [
        projectPlan?.storyMap?.cards,
      ]
    );

  const [cards, setCards] =
    useState(persistedCards);

  const [saving, setSaving] =
    useState(false);

  const [saveError, setSaveError] =
    useState("");

  const [saveMessage, setSaveMessage] =
    useState("");

  useEffect(() => {
    setCards(persistedCards);
    setSaveError("");
    setSaveMessage("");
  }, [persistedCards]);

  const revision =
    Number(projectPlan?.revision || 0);

  const dirty =
    !sameCards(
      cards,
      persistedCards
    );

  function clearSaveState() {
    setSaveError("");
    setSaveMessage("");
  }

  function addCard() {
    if (!canEdit) {
      return;
    }

    clearSaveState();

    setCards(
      (current) => [
        ...current,
        {
          cardId: newCardId(),
          title: "",
          summary: "",
        },
      ]
    );
  }

  function updateCard(
    cardId,
    field,
    value
  ) {
    if (!canEdit) {
      return;
    }

    clearSaveState();

    setCards(
      (current) =>
        current.map(
          (card) =>
            card.cardId === cardId
              ? {
                  ...card,
                  [field]: value,
                }
              : card
        )
    );
  }

  function removeCard(cardId) {
    if (!canEdit) {
      return;
    }

    clearSaveState();

    setCards(
      (current) =>
        current.filter(
          (card) =>
            card.cardId !== cardId
        )
    );
  }

  function moveCard(
    index,
    direction
  ) {
    if (!canEdit) {
      return;
    }

    const nextIndex =
      index + direction;

    if (
      nextIndex < 0 ||
      nextIndex >= cards.length
    ) {
      return;
    }

    clearSaveState();

    setCards((current) => {
      const next =
        [...current];

      const [moved] =
        next.splice(
          index,
          1
        );

      next.splice(
        nextIndex,
        0,
        moved
      );

      return next;
    });
  }

  async function submit(event) {
    event?.preventDefault?.();

    if (
      !canEdit ||
      saving ||
      !dirty ||
      revision < 1 ||
      typeof onSave !== "function"
    ) {
      return;
    }

    try {
      setSaving(true);
      setSaveError("");
      setSaveMessage("");

      await onSave(
        {
          cards:
            normalizeCards(cards),
        },
        revision
      );

      setSaveMessage(
        isFR
          ? "Story Map enregistrée."
          : "Story Map saved."
      );
    } catch (error) {
      setSaveError(
        clean(error?.message) ||
        (
          isFR
            ? "Impossible d’enregistrer la Story Map."
            : "Could not save the Story Map."
        )
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <form
      className="ns-card"
      onSubmit={submit}
      style={{
        padding: 22,
        borderRadius: 20,
        border:
          "1.5px solid rgba(220,38,38,0.24)",
        marginTop: 18,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent:
            "space-between",
          alignItems:
            "flex-start",
          gap: 16,
          flexWrap: "wrap",
        }}
      >
        <div
          style={{
            maxWidth: 760,
          }}
        >
          <div
            style={{
              fontSize: 12,
              fontWeight: 900,
              letterSpacing:
                "0.1em",
              textTransform:
                "uppercase",
              color: "#dc2626",
            }}
          >
            STORY MAP
          </div>

          <h3
            className="ns-card-title"
            style={{
              margin:
                "6px 0 8px",
              fontSize: 25,
            }}
          >
            {isFR
              ? "Tracez le mouvement de votre histoire"
              : "Map the movement of your story"}
          </h3>

          <p
            className="ns-card-meta"
            style={{
              margin: 0,
              lineHeight: 1.6,
              maxWidth: 720,
            }}
          >
            {isFR
              ? "Ajoutez les moments qui comptent, dans l’ordre où l’histoire se déroule. Vous pourrez structurer davantage plus tard."
              : "Add the moments that matter, in the order the story unfolds. You can add more structure later."}
          </p>
        </div>

        <button
          className="ns-button ns-button-outline"
          type="button"
          onClick={addCard}
          disabled={saving || !canEdit}
        >
          {isFR
            ? "+ Ajouter un moment"
            : "+ Add story moment"}
        </button>
      </div>

      {!canEdit ? (
        <div
          className="ns-card ns-card-soft"
          role="status"
          style={{
            marginTop: 18,
            padding: 14,
            borderRadius: 14,
          }}
        >
          <strong>
            {isFR
              ? "Lecture seule"
              : "Read only"}
          </strong>
          <div
            className="ns-card-meta"
            style={{
              marginTop: 4,
            }}
          >
            {isFR
              ? "Seul le propriétaire peut modifier la Story Map dans cette version."
              : "Only the project owner can edit the Story Map in this version."}
          </div>
        </div>
      ) : null}

      {cards.length === 0 ? (
        <div
          className="ns-card ns-card-soft"
          style={{
            marginTop: 18,
            padding: 18,
            borderRadius: 16,
          }}
        >
          <strong>
            {isFR
              ? "Votre Story Map est encore vide."
              : "Your Story Map is empty."}
          </strong>

          <p
            className="ns-card-meta"
            style={{
              margin:
                "7px 0 14px",
              lineHeight: 1.5,
            }}
          >
            {isFR
              ? "Commencez par un moment important : une arrivée, une décision, une découverte, un retournement ou une fin."
              : "Start with an important moment: an arrival, decision, discovery, turning point, or ending."}
          </p>

          <button
            className="ns-button ns-button-primary"
            type="button"
            onClick={addCard}
            disabled={saving || !canEdit}
          >
            {isFR
              ? "Ajouter le premier moment"
              : "Add first story moment"}
          </button>
        </div>
      ) : (
        <div
          style={{
            display: "grid",
            gap: 12,
            marginTop: 18,
          }}
        >
          {cards.map(
            (card, index) => (
              <article
                key={card.cardId}
                className="ns-card ns-card-soft"
                style={{
                  padding: 16,
                  borderRadius: 16,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent:
                      "space-between",
                    alignItems:
                      "center",
                    gap: 12,
                    flexWrap:
                      "wrap",
                    marginBottom: 12,
                  }}
                >
                  <strong>
                    {isFR
                      ? `Moment ${index + 1}`
                      : `Story moment ${index + 1}`}
                  </strong>

                  <div
                    style={{
                      display: "flex",
                      gap: 8,
                      flexWrap:
                        "wrap",
                    }}
                  >
                    <button
                      className="ns-button ns-button-outline"
                      type="button"
                      onClick={() =>
                        moveCard(
                          index,
                          -1
                        )
                      }
                      disabled={
                        !canEdit ||
                        saving ||
                        index === 0
                      }
                    >
                      {isFR
                        ? "Plus tôt"
                        : "Move earlier"}
                    </button>

                    <button
                      className="ns-button ns-button-outline"
                      type="button"
                      onClick={() =>
                        moveCard(
                          index,
                          1
                        )
                      }
                      disabled={
                        !canEdit ||
                        saving ||
                        index ===
                          cards.length - 1
                      }
                    >
                      {isFR
                        ? "Plus tard"
                        : "Move later"}
                    </button>

                    <button
                      className="ns-button ns-button-outline"
                      type="button"
                      onClick={() =>
                        removeCard(
                          card.cardId
                        )
                      }
                      disabled={saving || !canEdit}
                    >
                      {isFR
                        ? "Supprimer"
                        : "Remove"}
                    </button>
                  </div>
                </div>

                <label
                  style={{
                    display: "grid",
                    gap: 7,
                  }}
                >
                  <span
                    style={{
                      fontWeight: 800,
                    }}
                  >
                    {isFR
                      ? "Nom du moment"
                      : "Moment title"}
                  </span>

                  <input
                    className="ns-input"
                    value={card.title}
                    disabled={saving || !canEdit}
                    onChange={(event) =>
                      updateCard(
                        card.cardId,
                        "title",
                        event.target.value
                      )
                    }
                    placeholder={
                      isFR
                        ? "Ex. La lettre arrive"
                        : "e.g. The letter arrives"
                    }
                  />
                </label>

                <label
                  style={{
                    display: "grid",
                    gap: 7,
                    marginTop: 12,
                  }}
                >
                  <span
                    style={{
                      fontWeight: 800,
                    }}
                  >
                    {isFR
                      ? "Que se passe-t-il ?"
                      : "What happens?"}
                  </span>

                  <textarea
                    className="ns-input"
                    rows={4}
                    value={card.summary}
                    disabled={saving || !canEdit}
                    onChange={(event) =>
                      updateCard(
                        card.cardId,
                        "summary",
                        event.target.value
                      )
                    }
                    placeholder={
                      isFR
                        ? "Décrivez brièvement ce qui change dans ce moment."
                        : "Briefly describe what changes in this moment."
                    }
                    style={{
                      resize: "vertical",
                      minHeight: 105,
                    }}
                  />
                </label>
              </article>
            )
          )}
        </div>
      )}

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          flexWrap: "wrap",
          marginTop: 18,
        }}
      >
        <button
          className="ns-button ns-button-primary"
          type="submit"
          disabled={
            !canEdit ||
            saving ||
            !dirty ||
            revision < 1
          }
        >
          {saving
            ? (
                isFR
                  ? "Enregistrement…"
                  : "Saving…"
              )
            : (
                isFR
                  ? "Enregistrer la Story Map"
                  : "Save Story Map"
              )}
        </button>

        {!dirty &&
        !saveMessage &&
        !saveError ? (
          <span
            className="ns-card-meta"
          >
            {isFR
              ? "Aucune modification non enregistrée."
              : "No unsaved changes."}
          </span>
        ) : null}

        {saveMessage ? (
          <span
            style={{
              fontWeight: 800,
              color: "#166534",
            }}
          >
            {saveMessage}
          </span>
        ) : null}

        {saveError ? (
          <span
            style={{
              fontWeight: 800,
              color: "#b91c1c",
            }}
          >
            {saveError}
          </span>
        ) : null}
      </div>
    </form>
  );
}
